/* ==========================================================================
   Adventoria World Path — adventoria.js
   World switcher · header · mobile menu · cart drawer (AJAX) · product UI
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  var WORLDS = ['dino', 'space', 'princess'];

  /* ------------------------------------------------------------------
     World switcher
     ------------------------------------------------------------------ */
  /* The world change is carried entirely by the five registered @property
     tokens in base.css and their 180 ms :root transition. There is no
     'world-switching' class and nothing here needs one.

     `opts.persist === false` changes the world WITHOUT writing it down. The
     worlds section's one auto-played exchange uses it: the saved world must be
     a choice the visitor made, not one the page made while they scrolled. */
  function setWorld(world, opts) {
    if (WORLDS.indexOf(world) === -1) return;
    root.setAttribute('data-world', world);
    if (!opts || opts.persist !== false) {
      try {
        localStorage.setItem('adventoria:world', world);
      } catch (e) { /* private mode */ }
    }

    doc.querySelectorAll('[data-world-btn]').forEach(function (btn) {
      var active = btn.getAttribute('data-world-btn') === world;
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    doc.dispatchEvent(new CustomEvent('adventoria:world', { detail: { world: world } }));
  }

  doc.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-world-btn]');
    if (!btn) return;
    setWorld(btn.getAttribute('data-world-btn'));
  });

  // Sync buttons with the world applied pre-paint in theme.liquid
  setWorld(root.getAttribute('data-world') || 'dino');

  /* THE WORLD-STATE HOOK. This file owns the world: the attribute, localStorage,
     the [data-world-btn] mirror and the adventoria:world event. assets/world-swap.js
     borrows the one function rather than keeping a second copy of the truth,
     which is why a swap fired from a chip and a world restored from storage can
     never disagree. */
  window.Adventoria = window.Adventoria || {};
  window.Adventoria.setWorld = setWorld;

  /* ------------------------------------------------------------------
     Header scroll shadow
     ------------------------------------------------------------------ */
  var header = doc.querySelector('.site-header');
  if (header) {
    var onScroll = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ------------------------------------------------------------------
     Mobile menu
     ------------------------------------------------------------------ */
  var mobileMenu = doc.getElementById('MobileMenu');

  function toggleMenu(open) {
    if (!mobileMenu) return;
    mobileMenu.setAttribute('data-open', open ? 'true' : 'false');
    doc.body.style.overflow = open ? 'hidden' : '';
    var toggle = doc.querySelector('[data-menu-open]');
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      var first = mobileMenu.querySelector('a, button');
      if (first) first.focus();
    }
  }

  doc.addEventListener('click', function (e) {
    if (e.target.closest('[data-menu-open]')) toggleMenu(true);
    if (e.target.closest('[data-menu-close]')) toggleMenu(false);
  });

  /* ------------------------------------------------------------------
     Cart drawer + AJAX cart
     ------------------------------------------------------------------ */
  var drawer = doc.getElementById('CartDrawer');
  var routes = window.AdventoriaRoutes || {};
  var strings = window.AdventoriaStrings || {};
  var lastFocused = null;

  function openDrawer() {
    if (!drawer) return;
    lastFocused = doc.activeElement;
    drawer.setAttribute('data-open', 'true');
    drawer.removeAttribute('aria-hidden');
    doc.body.style.overflow = 'hidden';
    var closeBtn = drawer.querySelector('[data-cart-close]');
    if (closeBtn) closeBtn.focus();
  }

  /* A dialog with aria-modal="true" that lets Tab walk out into the page behind
     it is lying about being modal (QA: "Keyboard Navigation", High). */
  var DRAWER_FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])';

  doc.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || !drawer || drawer.getAttribute('data-open') !== 'true') return;
    var panel = drawer.querySelector('.cart-drawer__panel');
    if (!panel) return;
    var items = Array.prototype.filter.call(panel.querySelectorAll(DRAWER_FOCUSABLE), function (el) {
      return el.offsetParent !== null;
    });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && doc.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && doc.activeElement === last) {
      e.preventDefault();
      first.focus();
    } else if (!panel.contains(doc.activeElement)) {
      e.preventDefault();
      first.focus();
    }
  });

  function closeDrawer() {
    if (!drawer) return;
    drawer.setAttribute('data-open', 'false');
    drawer.setAttribute('aria-hidden', 'true');
    doc.body.style.overflow = '';
    if (lastFocused) lastFocused.focus();
  }

  doc.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      openDrawer();
    }
    if (e.target.closest('[data-cart-close]') || e.target.matches('.cart-drawer__overlay')) {
      closeDrawer();
    }
  });

  doc.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      closeDrawer();
      toggleMenu(false);
    }
  });

  function money(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CZK';
    try {
      return new Intl.NumberFormat(doc.documentElement.lang || 'cs', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(0) + ' ' + currency;
    }
  }

  /* ------------------------------------------------------------------
     THE CART IS RENDERED ONCE, IN LIQUID, AND RE-RENDERED FROM THE SAME
     MARKUP.

     This function used to build a cart line out of a 25-line string of
     concatenated HTML with its accessible names written in English inside
     this file — aria-label="-", aria-label="+", aria-label="Qty" — and a
     remove control that was a bare "&times;" with no name at all. So the
     drawer a visitor saw on load and the drawer they saw one click later
     were two different components, and only one of them was translated.

     Now snippets/cart-line.liquid renders both: the server-side lines AND
     the <template data-cart-line> this clones. Nothing is built from
     strings, so a product title can never reach innerHTML, and every label
     comes from locales/cs.default.json.
     ------------------------------------------------------------------ */
  var cartStrings = (function () {
    var el = doc.querySelector('[data-cart-strings]');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  })();

  /* Czech needs three forms and Shopify's locale files have no plural
     support, so the form is chosen here exactly as the Liquid chooses it. */
  function pluralKey(prefix, n) {
    if (n === 1) return prefix + '_one';
    if (n > 1 && n < 5) return prefix + '_few';
    return prefix + '_many';
  }

  function phrase(key, count, total) {
    var s = cartStrings[key];
    if (!s) return '';
    return s.replace('%COUNT%', count).replace('%TOTAL%', total);
  }

  /* An aria-label that names its product. The pattern travels on the element
     as data-label-tpl so this file needs to know no Czech at all. */
  function relabel(el, title) {
    if (!el) return;
    var tpl = el.getAttribute('data-label-tpl');
    if (tpl) el.setAttribute('aria-label', tpl.replace('%TITLE%', title));
  }

  function fillLine(node, item) {
    var title = item.product_title || item.title || '';

    node.setAttribute('data-line-key', item.key);

    var img = node.querySelector('[data-line-img]');
    var noimg = node.querySelector('[data-line-noimg]');
    if (item.image && img) {
      img.src = item.image.replace(/(\.[a-z]{3,4})(\?|$)/, '_160x160$1$2');
      img.hidden = false;
      if (noimg) noimg.remove();
    } else if (img) {
      img.remove();
    }

    var titleEl = node.querySelector('[data-line-title]');
    if (titleEl) {
      titleEl.textContent = title;
      titleEl.href = item.url;
    }

    var variantEl = node.querySelector('[data-line-variant]');
    if (variantEl) {
      var hasVariant = item.variant_title && item.variant_title !== 'Default Title';
      variantEl.textContent = hasVariant ? item.variant_title : '';
      variantEl.hidden = !hasVariant;
    }

    var qty = node.querySelector('[data-line-qty]');
    if (qty) qty.value = item.quantity;

    relabel(qty, title);
    relabel(node.querySelector('[data-cart-qty="-1"]'), title);
    relabel(node.querySelector('[data-cart-qty="1"]'), title);
    relabel(node.querySelector('[data-cart-remove]'), title);

    var priceEl = node.querySelector('[data-line-price]');
    if (priceEl) priceEl.textContent = money(item.final_line_price);

    return node;
  }

  /* The free-delivery meter, and ONLY if a merchant set a threshold. The
     element does not exist otherwise, so there is nothing to update. */
  function renderShipping(cart) {
    doc.querySelectorAll('[data-cart-ship]').forEach(function (wrap) {
      var threshold = parseInt(wrap.getAttribute('data-ship-threshold') || '0', 10);
      if (!threshold) return;
      var remaining = threshold - cart.items_subtotal_price;
      var line = wrap.querySelector('[data-ship-line]');
      var fill = wrap.querySelector('[data-ship-fill]');
      if (line) {
        line.textContent = remaining > 0
          ? (cartStrings.ship_progress || '').replace('%AMOUNT%', money(remaining))
          : (cartStrings.ship_reached || '');
      }
      if (fill) {
        var pct = Math.min(100, Math.round((cart.items_subtotal_price * 100) / threshold));
        fill.style.width = pct + '%';
      }
    });
  }

  /* ONE ATOMIC CONTEXTUAL MESSAGE, never a bare number, and focus never
     moves (QA: "Contextual Live Badge Updates", High). The region is a
     sibling of the drawer rather than a child, because .cart-drawer is
     visibility:hidden when closed and a live region inside a hidden subtree
     is never announced at all. */
  function announce(cart, prefix) {
    var region = doc.querySelector('[data-cart-status]');
    if (!region) return;
    var text = cart.item_count === 0
      ? (cartStrings.status_empty || '')
      : phrase(pluralKey('status', cart.item_count), cart.item_count, money(cart.items_subtotal_price));
    region.textContent = prefix ? prefix + ' ' + text : text;
  }

  function renderCart(cart) {
    var subtotal = money(cart.items_subtotal_price);

    doc.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.classList.toggle('has-items', cart.item_count > 0);
    });

    /* Document-wide, not drawer-only: the cart PAGE prints the same subtotal
       in its summary, and it must not disagree with the drawer's. */
    doc.querySelectorAll('[data-cart-subtotal]').forEach(function (el) {
      el.textContent = subtotal;
    });

    doc.querySelectorAll('[data-cart-count-text]').forEach(function (el) {
      el.textContent = cart.item_count === 0
        ? ''
        : phrase(pluralKey('count', cart.item_count), cart.item_count, subtotal);
    });

    renderShipping(cart);

    var itemsEl = drawer && drawer.querySelector('[data-cart-items]');
    var footEl = drawer && drawer.querySelector('.cart-drawer__foot');
    if (footEl) footEl.hidden = cart.item_count === 0;

    if (itemsEl) {
      var emptyTpl = drawer.querySelector('template[data-cart-empty]');
      var lineTpl = drawer.querySelector('template[data-cart-line]');

      itemsEl.textContent = '';

      if (cart.item_count === 0) {
        if (emptyTpl) itemsEl.appendChild(emptyTpl.content.cloneNode(true));
      } else if (lineTpl) {
        cart.items.forEach(function (item) {
          var frag = lineTpl.content.cloneNode(true);
          var node = frag.querySelector('.cart-line');
          if (node) fillLine(node, item);
          itemsEl.appendChild(frag);
        });
      }
    }

    /* The cart page mutates its own lines off this event rather than being
       re-rendered from here — the markup it owns has a name attribute the
       drawer's does not, and a shared re-render would have to know that. */
    doc.dispatchEvent(new CustomEvent('adventoria:cart', { detail: cart }));
  }

  function fetchCart() {
    return fetch((routes.cart_url || '/cart') + '.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        renderCart(cart);
        return cart;
      });
  }

  function changeLine(key, quantity) {
    return fetch(routes.cart_change_url || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        renderCart(cart);
        announce(cart);
        return cart;
      });
  }

  /* Quantity steppers and the labelled remove, in the drawer AND on the cart
     page — one delegated handler, because snippets/cart-line renders the same
     controls into both. */
  doc.addEventListener('click', function (e) {
    var line = e.target.closest('.cart-line');
    if (!line) return;
    var key = line.getAttribute('data-line-key');
    var input = line.querySelector('[data-cart-qty-input]');

    var stepBtn = e.target.closest('[data-cart-qty]');
    if (stepBtn && input) {
      var next = Math.max(0, parseInt(input.value || '0', 10) + parseInt(stepBtn.getAttribute('data-cart-qty'), 10));
      input.value = next;
      changeLine(key, next);
    }

    if (e.target.closest('[data-cart-remove]')) {
      changeLine(key, 0);
    }
  });

  doc.addEventListener('change', function (e) {
    if (!e.target.matches('[data-cart-qty-input]')) return;
    var line = e.target.closest('.cart-line');
    if (!line) return;
    changeLine(line.getAttribute('data-line-key'), Math.max(0, parseInt(e.target.value || '0', 10)));
  });

  /* ADD TO CART — the product page's buy button and every quick-add on a card
     go through this one handler. The form is a real POST to /cart/add with a
     real hidden `id`, so with no JavaScript the browser posts it and Shopify
     redirects to /cart; with JavaScript the post is intercepted, the drawer
     opens on the new line and the change is announced. */
  /* Swap a buy button's label to its confirmation for one beat, then put the
     merchant's own word back. Guarded on a timer id kept on the element so a
     reader who presses twice does not end up with "Přidáno" as the permanent
     label of the button. */
  function confirmAdd(btn) {
    if (!btn) return;
    var label = btn.querySelector('.btn__label') || btn;
    var done = btn.getAttribute('data-added-label') || strings.added;
    if (!done) return;
    if (btn._addedTimer) {
      clearTimeout(btn._addedTimer);
    } else {
      btn._addedText = label.textContent;
    }
    label.textContent = done;
    btn.classList.add('is-added');
    btn._addedTimer = setTimeout(function () {
      label.textContent = btn._addedText;
      btn.classList.remove('is-added');
      btn._addedTimer = null;
    }, 1800);
  }

  doc.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();

    var btn = form.querySelector('[type="submit"]');
    if (btn) {
      btn.classList.add('btn--loading');
      btn.setAttribute('aria-busy', 'true');
    }

    fetch(routes.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: new FormData(form)
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(function () { return fetchCart(); })
      .then(function (cart) {
        openDrawer();
        announce(cart, cartStrings.opened);
        /* THE THIRD CONFIRMATION, AND THE ONE THE PRESSING FINGER SEES.

           The drawer is the answer, the live region is the spoken answer, and
           this is the answer on the control itself: the label reads "Přidáno"
           for one beat before returning. It exists because a card's add button
           used to be a bare red square that did its work in silence — a reader
           who missed the drawer opening had no way to tell a working button
           from a dead one. The old text is restored from the element, so a
           button whose label a merchant renamed comes back saying their word
           and not ours. */
        confirmAdd(btn);
      })
      .catch(function () {
        var err = form.querySelector('[data-form-error]');
        if (err) {
          err.hidden = false;
          err.textContent = strings.cartError || 'Something went wrong. Please try again.';
        }
      })
      .finally(function () {
        if (btn) {
          btn.classList.remove('btn--loading');
          btn.removeAttribute('aria-busy');
        }
      });
  });

  /* ------------------------------------------------------------------
     Product gallery — NOT HERE ANY MORE.

     The stock theme's thumbnail strip lived here: a delegated click that swapped
     the src of one <img> in [data-gallery-main]. The product page is now a
     horizontal scroll-snap carousel with a dot pill (assets/product-gallery.js,
     loaded only by the section that has one), because not one shop in the
     reference set ships a thumbnail rail. Nothing renders [data-gallery-thumb]
     or [data-gallery-main], so the handler could never fire and is gone rather
     than left to be resurrected by a copy-paste.
     ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------
     Variant picker — update price / availability / id
     ------------------------------------------------------------------ */
  doc.querySelectorAll('[data-variant-json]').forEach(function (script) {
    var container = script.closest('[data-product-container]');
    if (!container) return;
    var variants;
    try {
      variants = JSON.parse(script.textContent);
    } catch (e) { return; }

    container.addEventListener('change', function (e) {
      if (!e.target.matches('[data-option-input]')) return;
      var selected = [];
      container.querySelectorAll('fieldset[data-option-index]').forEach(function (fs) {
        var checked = fs.querySelector('input:checked');
        if (checked) selected.push(checked.value);
      });

      var match = variants.find(function (v) {
        return v.options.every(function (opt, i) { return opt === selected[i]; });
      });
      if (!match) return;

      /* SCOPED TO THE PAGE'S OWN FORM, NOT TO THE FIRST FORM IN THE CONTAINER.

         The product page now carries cross-sell rows above the buy button, and
         each of those is its own <form> with its own hidden `id` — so a bare
         container.querySelector('input[name="id"]') found the FIRST add-on's
         input and a variant change quietly rewrote which product the add-on
         button would put in the basket. [data-main-form] marks the one form
         that sells the product this page is about. The unscoped lookup stays as
         a fallback so older markup without the attribute keeps working. */
      var mainForm = container.querySelector('[data-main-form]');
      var idInput = (mainForm || container).querySelector('input[name="id"]');
      if (idInput) idInput.value = match.id;

      var priceEl = container.querySelector('[data-price]');
      if (priceEl) priceEl.textContent = money(match.price);

      var compareEl = container.querySelector('[data-compare-price]');
      if (compareEl) {
        var onSale = match.compare_at_price && match.compare_at_price > match.price;
        compareEl.hidden = !onSale;
        if (onSale) compareEl.textContent = money(match.compare_at_price);
        if (priceEl) priceEl.classList.toggle('price__sale', !!onSale);
      }

      var submit = (mainForm || container).querySelector('[type="submit"]');
      if (submit) {
        submit.disabled = !match.available;
        var label = submit.querySelector('.btn__label');
        if (label) {
          label.textContent = match.available ? (strings.addToCart || 'Add to cart') : (strings.soldOut || 'Sold out');
        }
      }
    });
  });

  /* ------------------------------------------------------------------
     Generic quantity steppers (product page)
     ------------------------------------------------------------------ */
  doc.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-step]');
    if (!btn || btn.closest('.cart-line')) return;
    var wrap = btn.closest('.qty');
    var input = wrap && wrap.querySelector('input');
    if (!input) return;
    var min = parseInt(input.min || '1', 10);
    input.value = Math.max(min, parseInt(input.value || '1', 10) + parseInt(btn.getAttribute('data-qty-step'), 10));
  });
})();
